import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Switch,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import PageHeader from "components/layout/PageHeader";
import StepDescription from "components/onboarding/StepDescription";
import StepTitle from "components/onboarding/StepTitle";
import { useFeatures } from "hooks/useFeatures";
import { useRoles } from "hooks/useRoles";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "utils/api";

const AddMembers = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { user } = useTenant();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [isGuarantor, setIsGuarantor] = useState(false);
  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState(false);

  const { hasFeature } = useFeatures();
  const hasBankAccounts = hasFeature("connect_bank_accounts");

  const { roles } = useRoles({
    company_id: user?.company?.id,
  });

  const guarantorRole = useMemo(() => {
    return roles?.find((role: any) => role.name === "Guarantor");
  }, [roles]);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      await apiRequest("/supabase/invite", {
        first_name: firstName,
        last_name: lastName,
        email,
        guarantor: isGuarantor,
        role: isGuarantor ? guarantorRole?.id : "",
        type: "borrower",
      });
    },
    onSuccess: () => {
      setFirstName("");
      setLastName("");
      setEmail("");
      setAdding(false);
      setIsGuarantor(false);
      setAdded(true);
      toast({
        title: "Invite Sent",
        description: "We've sent an email to your team member!",
        status: "success",
      });
    },
    onError: (err: any) => {
      toast({
        title: "Invite Failed",
        description: err.response.data,
        status: "error",
      });
    },
  });

  function handleSkip() {
    if (hasBankAccounts) {
      navigate("/setup/bank");
    } else {
      navigate("/setup/documents");
    }
  }

  return (
    <Box py="34px" px="26px" flex="1">
      <PageHeader title="Add Members" hideUser />
      <Divider mt="40px" mb="60px" />
      <StepTitle>Add members to your team</StepTitle>
      <StepDescription>
        You can add members to your account and decide their preferences and user permissions.
      </StepDescription>
      {adding ? (
        <Box maxW="511px">
          <HStack spacing="24px" mb="24px">
            <FormControl flex="1">
              <FormLabel>First Name</FormLabel>
              <Input
                value={firstName}
                onChange={ev => setFirstName(ev.target.value)}
                // isInvalid={invalidFields.includes("fundingDate")}
                placeholder="Jane"
                variant="primary"
              />
            </FormControl>
            <FormControl flex="1">
              <FormLabel>Last Name</FormLabel>
              <Input
                value={lastName}
                onChange={ev => setLastName(ev.target.value)}
                // isInvalid={invalidFields.includes("fundingDate")}
                placeholder="Doe"
                variant="primary"
              />
            </FormControl>
          </HStack>
          <FormControl mb="24px">
            <FormLabel>Email Address</FormLabel>
            <Input
              value={email}
              onChange={ev => setEmail(ev.target.value)}
              // isInvalid={invalidFields.includes("fundingDate")}
              placeholder="jane@doe.com"
              variant="primary"
            />
          </FormControl>
          <FormControl>
            <FormLabel>Are they a guarantor?</FormLabel>
            <Flex alignItems="center">
              <Switch
                isChecked={isGuarantor}
                onChange={ev => setIsGuarantor(ev.target.checked)}
                id="is-guarantor"
                mr="12px"
              />
              <FormLabel htmlFor="is-guarantor" mb="0">
                {isGuarantor ? "Yes" : "No"}
              </FormLabel>
            </Flex>
            <FormHelperText color="secondary" fontSize="14px" mt="18px">
              Until you add a guarantor you will be the guarantor on this account by default.
            </FormHelperText>
          </FormControl>
          <HStack mt="28px" justifyContent="space-between">
            <Button
              isDisabled={isPending}
              onClick={() => {
                navigate("/setup/bank");
              }}
              size="sm"
              variant="outline"
            >
              Skip
            </Button>
            <Button
              isDisabled={isPending}
              size="sm"
              mr="16px"
              onClick={() => {
                mutate();
              }}
            >
              Send Invite
            </Button>
          </HStack>
        </Box>
      ) : (
        <Box>
          <Button size="sm" mr="16px" onClick={() => setAdding(true)}>
            {added ? "Add Another" : "Add Members"}
          </Button>
          <Button mr={4} onClick={handleSkip} size="sm" variant="outline">
            {added ? "Continue" : "Skip"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AddMembers;
